import {
  isPdfFieldAnswered,
  isServerFilledPdfField,
  toDeviceRect,
  type PdfField,
  type PdfTemplateVersion,
} from '@vigeo/shared';

/**
 * Where each field's input sits on the rendered page.
 *
 * Pure, and separated from the component on purpose: this is the half that can be wrong by a
 * few points, and a few points is a signature off the line. Keeping it out of React means it
 * can be tested against fixed numbers rather than through a canvas that jsdom cannot draw.
 *
 * It calls the SAME `toDeviceRect` the server fill uses. That is the whole point of #58 —
 * two implementations drift, and the drift is invisible until somebody holds the printout
 * against the screen.
 */

export interface PositionedField {
  readonly field: PdfField;
  /** CSS pixels from the top-left of the rendered page, ready for absolute positioning. */
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  /** Clamped so a field never renders unreadably small on a zoomed-out page. */
  readonly fontSize: number;
}

/** iOS Safari zooms the whole page when a focused input is under 16px. */
export const MIN_INPUT_FONT_PX = 16;

export function positionFields(
  version: PdfTemplateVersion,
  pageIndex: number,
  scale: number,
): readonly PositionedField[] {
  const page = version.pages[pageIndex];
  if (page === undefined) return [];

  return version.fields
    // A server-filled field is not an input: the portal must never supply what the server
    // prints (ADR-014 §1). Showing its value to the person is #217.
    .filter((field) => field.page === pageIndex && !isServerFilledPdfField(field))
    .map((field) => {
      const box = toDeviceRect(field.rect, {
        mediaBox: page.mediaBox,
        rotation: page.rotation,
        scale,
      });
      return {
        field,
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
        // The mapped size scaled, but never small enough to make iOS zoom the page and throw
        // the rest of the form off-screen.
        fontSize: Math.max(MIN_INPUT_FONT_PX, field.fontSize * scale),
      };
    });
}

/**
 * Tab order: down the page, then across.
 *
 * Field map order is authoring order, which follows whatever the mapping pass happened to
 * click. A keyboard user tabbing through a form in that order is being led around the page at
 * random, and the canvas is `aria-hidden`, so tab order IS the document order for anyone not
 * looking at it.
 */
export function inTabOrder(fields: readonly PositionedField[]): readonly PositionedField[] {
  return [...fields].sort((a, b) => {
    // A row's worth of tolerance, so two fields side by side are not ordered by a pixel.
    const rowGap = Math.max(a.height, b.height) * 0.6;
    if (Math.abs(a.top - b.top) > rowGap) return a.top - b.top;
    return a.left - b.left;
  });
}

/** What a typed signature draws: the name, above the line it stands on. */
export function signatureLine(typedName: string): string {
  return typedName.trim();
}

/**
 * CSS pixels per PDF point, for the page pdf.js actually drew.
 *
 * Extracted from the render effect because that is where it was WRONG and unreachable by a
 * test. `page.view` is the UNROTATED MediaBox while `viewport.width` is already rotated, so
 * dividing one by the other on a /Rotate 90 page yields 792/612 = 1.29 rather than 1.0 —
 * putting every field on that page 29% out of place.
 *
 * Rotated pages are exactly the scanned state forms this feature exists for, so the error
 * would have been absent from every synthetic fixture and present on every real document.
 */
export function cssScaleFor(
  page: { readonly view: readonly number[]; readonly rotate: number },
  viewportWidth: number,
  devicePixelRatio: number,
): number {
  const rotated = ((page.rotate % 360) + 360) % 360;
  const swapped = rotated === 90 || rotated === 270;
  const width = swapped
    ? (page.view[3] ?? 0) - (page.view[1] ?? 0)
    : (page.view[2] ?? 0) - (page.view[0] ?? 0);
  if (width <= 0 || devicePixelRatio <= 0) return 1;
  return viewportWidth / devicePixelRatio / width;
}

/**
 * Which required fields have not been answered yet.
 *
 * Computed in the portal because only the portal holds the pinned template — the step's
 * config carries a template id and version, not a field list. The result is recorded in the
 * draft so `readyToSubmit` can gate the button without the kernel needing the template.
 *
 * The rule itself is `isPdfFieldAnswered` in packages/shared — LITERALLY the same function
 * the server fill gates on, not a matching copy. This had a copy, and the copy said "any
 * boolean answers anything", so a boolean under a required text field turned the submit
 * button on here and then filed a blank where the name belonged (#117). Two implementations
 * of one rule drift, and this one drifted into agreeing with the bug.
 */
export function unansweredRequired(
  version: PdfTemplateVersion,
  values: Readonly<Record<string, string | boolean | undefined>>,
): readonly string[] {
  return version.fields
    // The person cannot answer a server-filled field, so it can never be "still needed".
    .filter((field) => field.required && !isServerFilledPdfField(field))
    .filter((field) => !isPdfFieldAnswered(field, values[field.id]))
    .map((field) => field.id);
}

/**
 * The draft a required checkbox needs before "no" can be sent, or null when it needs nothing.
 *
 * An untouched checkbox has no key in the draft — nothing seeds one — and an absent value is
 * not an answer, so the submit button never enabled for somebody who left "convicted of a
 * barrier crime" unticked. That is the correct answer for almost everyone. On screen the two
 * states are identical, because `checked={value === true}` renders an empty box whether the
 * value is `false` or missing, so the only route through was to tick the box and untick it
 * again — a gesture nobody would guess. The dead end pushed the person toward ticking a box
 * that says they have a criminal conviction, on a sworn disclosure, in order to send the form.
 *
 * Seeded rather than counting an absent required checkbox as answered. The draft is what the
 * submit body is built from, so seeding makes "no" a RECORDED answer: the filed evidence says
 * `false` where it used to say nothing at all, and nothing downstream has to guess whether an
 * absence meant no or meant unfinished.
 *
 * ONLY the portal does this, and only for REQUIRED checkboxes. The server must keep refusing
 * an absent required checkbox — a body that arrives without the key did not come from this
 * form, and seeding it server-side would invent an answer nobody gave. An optional checkbox
 * is left absent because absence is honest there: nobody was asked.
 *
 * Returns null rather than an equal copy when there is nothing to seed, so the caller can
 * write a draft only when the value actually changes.
 */
export function withRequiredCheckboxesSeeded(
  version: PdfTemplateVersion,
  values: Readonly<Record<string, string | boolean | undefined>>,
): Readonly<Record<string, string | boolean>> | null {
  const needed = version.fields.filter(
    (field) =>
      field.required &&
      field.kind === 'checkbox' &&
      !isServerFilledPdfField(field) &&
      // Not "is it absent". A checkbox's answer is a boolean and nothing else (#117), so a
      // string under one is not an answer being overwritten — it is a value this form could
      // never have produced, and leaving it would jam the button shut with nothing on screen
      // to explain why.
      typeof values[field.id] !== 'boolean',
  );
  if (needed.length === 0) return null;

  const seeded: Record<string, string | boolean> = {};
  for (const [id, value] of Object.entries(values)) {
    if (value !== undefined) seeded[id] = value;
  }
  for (const field of needed) seeded[field.id] = false;
  return seeded;
}
